<script lang="ts">
	import { LoadingSection, LoadingSpinner } from "misc-ui"
	import type { LuxAIController } from "../../LuxAIController.svelte"
	import type { Models as M } from "@lux-ai/api-interface"
	import { SearchBarInput } from "misc-ui"
	import ThemeInstance from "./ThemeInstance.svelte"
	import ThemeTagFilter from "./ThemeTagFilter.svelte"
	import { PlusIcon, SmallPlusIcon } from "luxedo-icons"
	import { Responsive } from "responsive"
	import { onMount } from "svelte"

	interface Props {
		controller: LuxAIController
	}

	let { controller }: Props = $props()

	let moreThemesAvailable = $derived(controller.moreThemesAvailable)

	let category: M.Collection = $derived(controller.selectedCategory)
	let searchInput: string = $derived(controller.searchInput)
	let collections: M.Collection[] = $derived(controller.availableCollections)

	let activeGenerations = $derived(controller.activeGenerations)
	let availableThemes = $derived(controller.availableThemes)

	let availableTags = $derived(controller.availableTags)
	let selectedTags = $derived(controller.selectedTags)

	let buttonContainer: HTMLDivElement = $state()

	const getRelevantActiveGeneration = (
		theme: M.Theme,
		activeGenerations: Array<M.ThemeGeneration>,
	) => activeGenerations.find((gen) => gen.theme_id === theme.id)

	async function handleSearch(searchStr: string) {
		isLoadingCategory = true
		await controller.setSearch(searchStr)
		isLoadingCategory = false
	}

	async function closeSearch() {
		isLoadingCategory = true
		await controller.setSearch(undefined)
		isLoadingCategory = false
	}

	let clearSearchInput: () => void = $state()

	let isLoadingCategory = $state(false)
	let lastCategory: string
	async function handleCategoryChange(e: Event & { currentTarget: HTMLSelectElement }) {
		isLoadingCategory = true
		const categoryId = e.currentTarget.value
		if (lastCategory === "FREE") {
			controller.deselectTag("free", true)
		}

		if (categoryId === "FREE") {
			controller.selectTag("free", true)
			await controller.setCategory(undefined)
		} else await controller.setCategory(e.currentTarget.value)
		lastCategory = categoryId
		isLoadingCategory = false
	}

	async function clearCategory() {
		isLoadingCategory = true
		await controller.setCategory(undefined)
		isLoadingCategory = false
	}

	async function clearFilter() {
		isLoadingCategory = true
		clearSearchInput()

		await controller.clearTags()
		await controller.setSearch(undefined)

		isLoadingCategory = false
	}

	let isLoading = $state(false)
	async function loadMore() {
		isLoading = true
		await controller.fetchNextThemes()
		isLoading = false
	}

	function onIntersection(entries: IntersectionObserverEntry[]) {
		if (entries[0].isIntersecting && moreThemesAvailable) {
			loadMore()
		}
	}

	onMount(() => {
		const observer = new IntersectionObserver(onIntersection)
		observer.observe(buttonContainer)
	})
</script>

<div class="theme-list" class:isMobile={Responsive.isMobile}>
	<div class="theme-list-header">
		<div class="select-container">
			<select name="" id="" onchange={handleCategoryChange} value={category?.id ?? "ALL"}>
				<option value="ALL">All Themes</option>
				<option value="FREE">Free Themes</option>
				{#each collections as collection}
					<option value={collection.id}>{collection.name}</option>
				{/each}
			</select>
		</div>

		<div class="filter-container">
			{#if searchInput || selectedTags.length}
				<button class="warn small icon" onclick={clearFilter} title="Clear filter"
					><SmallPlusIcon /></button
				>
			{/if}
			<ThemeTagFilter
				allTags={availableTags}
				{selectedTags}
				onSelect={controller.selectTag}
				onDeselect={controller.deselectTag}
			/>
			<SearchBarInput onSearch={handleSearch} onClose={closeSearch} bind:close={clearSearchInput} />
		</div>
	</div>

	<div class="themes">
		{#if availableThemes.length}
			{#each availableThemes as theme}
				{@const generation = getRelevantActiveGeneration(theme, activeGenerations)}
				<ThemeInstance {theme} activeGeneration={generation} {controller} />
			{/each}
		{:else if isLoadingCategory}
			<LoadingSection textContent="Loading themes" />
		{:else if searchInput || selectedTags.length}
			<div class="coming-soon">
				<span>
					{#if category}
						No matching themes found for that category... Click "Search All Categories" to widen
						your search.
					{:else}
						No matching themes found... Click "Clear Search" to clear your search.
					{/if}
				</span>
				{#if category}
					<button class="square-button outline-button" onclick={clearCategory}
						>View All Categories</button
					>
				{:else if !moreThemesAvailable}
					<button
						class="square-button outline-button"
						onclick={() => {
							clearFilter()
						}}>Clear Search</button
					>
				{/if}
			</div>
		{:else}
			<div class="coming-soon">
				<span> Our {category?.name} themes aren't ready yet. Check back soon! </span>
				<button class="square-button outline-button" onclick={clearCategory}>View All</button>
			</div>
		{/if}
	</div>

	<div class="button-container" bind:this={buttonContainer}>
		{#if !isLoadingCategory && isLoading}
			<LoadingSpinner height="2rem" color="var(--color-main)" />
		{/if}
		{#if !moreThemesAvailable}
			<span class="end-of-results">End of results</span>
		{/if}
	</div>
</div>

<style>
	.theme-list {
		display: flex;
		flex-direction: column;
	}

	.theme-list-header {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		padding: 0.5rem 0.75rem 0.5rem 0;
		background-color: var(--color-bg-dark);
		border-radius: var(--br);
		position: sticky;
		top: 0;
		z-index: 3;
		margin-bottom: 1rem;
	}

	.theme-list-header :global(.search-container) {
		background-color: var(--color-bg);
	}

	.filter-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-end;
		gap: 0.5rem;
	}

	select {
		font-size: var(--text-h4);
		border: none;
		background-color: unset;
		color: var(--color-main);
		font-family: var(--font-heading);
		transition: color var(--transition-duration) var(--transition-ease);
	}

	select:focus-visible,
	select:hover {
		color: var(--color-text-light);
		background-color: unset;
	}

	option {
		background-color: var(--color-bg-dark);
	}

	.isMobile select {
		font-size: var(--h2);
	}

	.button-container {
		margin-top: 2rem;
		height: 2.5rem;
	}

	.end-of-results {
		color: var(--color-text);
	}

	.themes {
		flex-grow: 1;
		min-height: 70vh;
	}

	.coming-soon {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		font-size: var(--h1);
		color: var(--color-text);
		gap: 1rem;
	}

	.isMobile .coming-soon {
		font-size: var(--h2);
		text-align: center;
		line-height: 1.2em;
	}

	button.icon {
		padding: 0;
	}

	button.icon :global(svg) {
		margin: 0;
		width: 1rem;
		height: 1rem;
		transform: rotate(45deg);
	}

	button.icon :global(.svg-stroke) {
		stroke: var(--color-text);
	}

	button.icon:hover :global(.svg-stroke),
	button.icon:focus-visible :global(.svg-stroke) {
		stroke: var(--color-error);
	}
</style>
